import { Banner, Button } from '@tackle-io/platform-ui';
import { Box, makeStyles } from 'vendor/material';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { MemberInput, MemberInputType } from '../../referralFormSchema';
import MemberInputFieldset from '../../MemberInputFieldset/MemberInputFieldset';
import FormDrawer from '../../components/FormDrawer';
import { fontWeightMediumLarge } from 'utils/fontWeightConstants';

interface MemberInputFormProps {
  onSubmit: (member: MemberInputType) => void;
  onCancel: () => void;
}

const useStyles = makeStyles((theme) => ({
  formContent: {
    display: 'grid',
    gap: theme.spacing(4),
    paddingBottom: theme.spacing(2),
  },
  formHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: theme.typography.pxToRem(16),
    lineHeight: theme.typography.pxToRem(24),
    fontWeight: fontWeightMediumLarge,
    color: theme.palette.NEUTRAL900,
  },
  banner: {
    marginBlock: theme.typography.pxToRem(40),
  },
}));

const MemberInputForm = ({ onSubmit, onCancel }: MemberInputFormProps) => {
  const classes = useStyles();
  const formMethods = useForm<MemberInputType>({
    resolver: zodResolver(MemberInput),
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
    },
  });

  const handleAddMember = (member: MemberInputType) => {
    onSubmit(member);
    formMethods.reset();
    onCancel();
  };

  const footerActions = (
    <>
      <Button appearance="primary" variant="outlined" onClick={onCancel}>
        Cancel
      </Button>
      <Button
        onClick={formMethods.handleSubmit((data) => {
          handleAddMember(data);
        })}
        appearance="primary"
        variant="contained"
        type="submit"
      >
        Add
      </Button>
    </>
  );

  return (
    <FormProvider {...formMethods}>
      <div style={{ height: '100%' }}>
        <FormDrawer
          footerActions={footerActions}
          title="Add Team member"
          onClose={onCancel}
        >
          <div style={{ height: '100%' }}>
            <Box className={classes.banner}>
              <Banner
                borderPosition="top"
                title="Microsoft requires all fields below to add a team member."
                type="info"
              />
            </Box>

            <div className={classes.formContent}>
              <div className={classes.formHeader}>
                <span className={classes.title}>Team Member</span>
              </div>
              <MemberInputFieldset />
            </div>
          </div>
        </FormDrawer>
      </div>
    </FormProvider>
  );
};

export default MemberInputForm;
